import React, { useState, useEffect, useMemo } from 'react'
import { Trash2 } from "lucide-react"
import { useParams } from "react-router-dom"
import { useQuery } from '@tanstack/react-query'
import { cn, queryTools, ELEMENTS } from "lib/utils"
import { useDispute } from "lib/units/dispute/hooks"
import { useCompositeQuery } from "lib/hooks"
import { RenderLayout } from "lib/components"
import { PresetsChooser } from "./presets"
import _ from 'lodash'


function OutputTypeBox({
  id,
  title,
  enabled=true,
  description,
  isSelected,
  onClick
}:{
  id:string,
  title:string,
  enabled?:boolean,
  description:string,
  isSelected:boolean,
  onClick:()=>void
}){
  return (
    <div 
      className={cn(
      "w-5/12 lg:w-full flex flex-col border border-solid border-transparent hover:border-teal-700 cursor-pointer px-3 py-2 select-none",
      isSelected ? "bg-teal-700" : "bg-transparent",
      enabled === false ? "opacity-20 cursor-not-allowed" : "cursor-pointer"
      )}
      onClick={ enabled == false ? () => null : onClick }
    >
      <h4>{ title }</h4>
      <p className="text-sm">{ description }</p>
    </div>
  )
}

export default function DisputePreviewChooser({
  setPreviewParams
}:{
  setPreviewParams:any
}){
  const { dspid } = useParams()

  const { data:_Dispute } = queryTools.resource.parseQueryForApi("dispute.findById", { _id: dspid }, { enabled: !!dspid })
  const saveFn = queryTools.resource.save(["dispute"])
  
  const { addPreset, removePreset } = useDispute({saveFn})

  const [ newParamset, setNewParamset ] = useState<any>({ subdomain: "", rp: "" })
  const [ tempPreviewParams, setTempPreviewParams ] = useState<any>({ dispute: dspid, subdomain: null, rp: null })
  const [ outputType, setOutputType ] = useState<string|null>(null)
  const [ iframeContent, setIframeContent ] = useState<string>()

  const [ presets, setPresets ] = useState<any>([])
  const [ newPreset, setNewPreset ] = useState<any>({ subdomain: "", rp: "" })

  const getPreviewViaFetch = async (params:any) => {
    const fetchPath = `/api/html/${_Dispute.Id}?rpid=${params.rp}&subdomain=${params.subdomain}&pdf=${outputType}`
    const response:any = await fetch(fetchPath, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });
    if (!response.ok) {
      throw new Error(`Error fetching preview: ${response.statusText}`);
    }
    if( outputType === "tb3" || outputType === "docraptor" ){
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      return;
    }
    if( outputType === "none" ){  
      const payload:any = await response.json();
      const html = payload.data.replace(/\n+/g, " ")
      setIframeContent(html);
    }
  }

  const generatePreview = () => {
    if( tempPreviewParams.subdomain && tempPreviewParams.rp && outputType === "standard" ){
      setPreviewParams({ ...tempPreviewParams })
    }
    else{
      getPreviewViaFetch(tempPreviewParams)
    }
  }

  const addNewPresetFn = () => {
    if( newParamset.subdomain && newParamset.rp ){
      addPreset(_Dispute, newParamset)
    }
    setNewParamset({ subdomain: "", rp: "" })
  }

  const outputTypes = [
    { id: "standard", enabled: true, title: "Standard Preview", description: "Usually matches Dispute Genius 100%; also has interactive features." },
    { id: "none", enabled: true, title: "Dispute Genius Html+CSS", description: "Exactly what is sent to Dispute Genius." },
    { id: "tb3", enabled: true, title: "TB3 PDF", description: "PDF generated completely by TB3." },
    { id: "docraptor", enabled: false, title: "DocRaptor PDF", description: "PDF generated completely by DocRaptor (in test mode)." }
  ]

  if( iframeContent ){
    return (
      <div className="">
        <div className="mm-html-preview">
          <iframe 
            srcDoc={iframeContent}
            sandbox="allow-scripts allow-same-origin"
            title="Dispute Preview" 
            style={{ width: "1100px", height: "900px", overflow: "auto" }}
          />
        </div>
        <div className="w-full flex justify-center items-center mt-4">
          <button
            className="px-4 py-2 bg-teal-700 text-white rounded hover:bg-teal-800"
            onClick={() => setIframeContent(undefined)}
          >
            Close Preview
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full flex flex-col pt-10 px-10">
      
      
      <div className="w-full flex flex-col lg:flex-row justify-center items-start gap-3">
        <div className="w-full lg:h-[24rem] flex flex-col border border-solid border-slate-700 p-[2px]">
          <h3 className="w-full bg-slate-700 px-2 py-1 uppercase text-sm">Choose a Preset</h3>

          <div className="w-full flex flex-col px-4 py-2 overflow-y-auto scrollbar relative">

            <div className="w-full grid grid-cols-12 gap-y-1">
              <div className="col-span-12 grid grid-cols-12 border-b border-solid border-slate-700 py-[.25rem] mb-1">
                <div className="col-span-2">Rep. Id</div>
                <div className="col-span-9">Subdomain</div>
                <div className="col-span-1"></div>
              </div>

              { _Dispute?.settings.presets.map( (preset:any) => (
                <div 
                  key={preset.refstr} 
                  onClick={() => setTempPreviewParams({...tempPreviewParams, subdomain: preset.subdomain, rp: preset.rp })}
                  className={cn(
                    "col-span-12 grid grid-cols-12 py-[3px] cursor-pointer border border-solid border-transparent hover:border-teal-700",
                    tempPreviewParams.subdomain === preset.subdomain && tempPreviewParams.rp === preset.rp && "bg-teal-700"
                  )}
                >
                  <div className="col-span-2 h-5 text-sm flex items-center justify-start pl-2">{ preset.rp }</div>
                  <div className="col-span-9 h-5 text-sm flex items-center px-1">
                    {`${preset.subdomain.slice(0,22)}${preset.subdomain.length > 22 ? "..." : ""}` }
                  </div>
                  <div className="col-span-1 h-5 pr-1 flex items-center ">
                    <Trash2 
                      onClick={(e:any) => {e.stopPropagation(); removePreset(_Dispute, preset.refstr)}} 
                      className="w-4 h-4 cursor-pointer" 
                    />
                  </div>
                </div>
              ))}

              <div className="col-span-12 grid grid-cols-12 gap-y-1 gap-x-5 mt-6">
                <div className="col-span-2">
                  <input 
                    type="text"
                    name="rp" 
                    className="w-[3rem] h-6 bg-transparent border border-solid border-slate-700 px-2 text-xs focus:outline-none focus:border-teal-700"
                    placeholder="RP Id"
                    value={newParamset.rp} 
                    onChange={(e:any) => setNewParamset({...newParamset, rp: e.target.value})} 
                  />
                </div>
                <div className="col-span-8">
                  <input 
                    type="text" 
                    name="subdomain" 
                    className="h-6 bg-transparent border border-solid border-slate-700 px-2 text-xs focus:outline-none focus:border-teal-700"
                    placeholder="Subdomain"
                    value={newParamset.subdomain} 
                    onChange={(e:any) => setNewParamset({...newParamset, subdomain: e.target.value})} 
                  />
                </div>
                <div className="col-span-2">
                  <button className="px-2 h-6 bg-slate-700 text-white text-xs rounded hover:bg-teal-800" onClick={addNewPresetFn}>
                    Add
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div className="w-full lg:h-[24rem] flex flex-col border border-solid border-slate-700">
          <h3 className="w-full bg-slate-700 px-2 py-1 uppercase text-sm">Choose the Output</h3>
          <div className="w-full flex flex-wrap px-4 py-2 gap-3">
            {outputTypes.map((type) => (
              <OutputTypeBox 
                key={type.id}
                id={type.id}
                enabled={type.enabled}
                title={type.title}
                description={type.description}
                isSelected={outputType === type.id}
                onClick={() => setOutputType(type.id)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="w-full flex justify-center items-center mt-4">
        <button
          className={cn(
            "px-4 py-2 bg-teal-700 text-white rounded hover:bg-teal-800",
            !tempPreviewParams.subdomain || !tempPreviewParams.rp || !outputType ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          )}
          onClick={generatePreview}
          disabled={!tempPreviewParams.subdomain || !tempPreviewParams.rp || !outputType} 
        >
          Generate Preview
        </button>
      </div>
    </div>
  )
}